// Reads an import CSV into the facts the import modal shows and the backend
// needs. The file's shape is TWO header rows, not one:
//
//   row 1  column names   →  Site, Manager, Opened
//   row 2  their dataTypes →  text, text,    dateTime
//   row 3+ the records
//
// so a file whose "41 records" the modal reports actually parsed 43 rows. Both
// header rows are the backend's contract, which is why nothing here invents
// column types.
//
// papaparse does the reading. It was already a dependency (unused until now) and
// hand-rolling this would be a mistake: quoted fields containing commas or
// newlines, CRLF endings and a leading BOM all have to work, and the per-cell
// validation below leans on the same parse.
//
// validateDataSourceCsv holds the whole rule set — file shape, then the type
// row, then every cell against its declared type. It runs BEFORE the import
// modal opens, so a file that reaches the modal is known-good.

import Papa from 'papaparse';
import { COLUMN_TYPES } from './columnTypes';

/** Reject anything larger up front: the parse below is synchronous, so a big
 *  file would freeze the tab rather than merely being slow. */
export const MAX_CSV_BYTES = 5 * 1024 * 1024;

export interface ParsedDataSourceCsv {
  /** Row 1, trimmed. */
  columnNames: string[];
  /** Row 2, trimmed — parallel to columnNames. */
  dataTypes: string[];
  /** Rows after the two headers. */
  recordCount: number;
  columnCount: number;
}

// ---- cell formats ----
// The per-cell rules the importer enforces. They live here, beside the code that
// applies them, rather than in the template module — the template's job is to
// SATISFY these, not to define them.

/** "lat, lng" — comma AND space, positive values only. Inherited verbatim from
 *  the legacy importer (`validateDataRows`). The embedded comma is why a
 *  geoCoordinates cell always ends up quoted in a CSV. */
export const geoCoordinatesRegex = /^\d+(\.\d+)?,\s\d+(\.\d+)?$/;

/** "+<digits>-<digits>" — a country code, a hyphen, then the number. Note the
 *  hyphen is REQUIRED, so an unbroken `+966551234567` is not valid. */
export const phoneRegex = /^\+\d+-\d+$/;

/** The SHAPE of a dateTime cell: zero-padded month and day, four-digit year,
 *  slash-separated. Shape only — `isValidMMDDYYYY` still has to range-check the
 *  parts, because this happily matches 13/32/2026. */
const mmddyyyyRegex = /^(\d{2})\/(\d{2})\/(\d{4})$/;

/** `MM/DD/YYYY`, and a date that actually exists on the calendar: 13/01/2026 and
 *  02/30/2026 are both refused, and February is leap-aware (02/29/2024 passes,
 *  02/29/2026 does not).
 *
 *  Restored from the legacy importer (`validateDataRows`), which is what the
 *  backend's own reader expects. A bare `new Date(value)` is NOT a substitute:
 *  it accepts "1200" as the year 1200, "2026-06-24", "Jun 24 2026" and much else
 *  the backend would then reject — and because a filled-in template is written
 *  through verbatim, anything we wave past here fails later and further away. */
export const isValidMMDDYYYY = (value: string): boolean => {
  const match = mmddyyyyRegex.exec(value);
  if (!match) return false;
  const [, rawMonth, rawDay, rawYear] = match;
  const month = Number(rawMonth);
  const day = Number(rawDay);
  if (month < 1 || month > 12 || day < 1) return false;
  // Day 0 of the FOLLOWING month is the last day of this one, so one probe
  // covers every month length, February in a leap year included, with no table.
  // setFullYear rather than the `new Date(year, …)` constructor: that constructor
  // maps years 0-99 onto 1900-1999, and the regex above admits "0050".
  const lastOfMonth = new Date(0);
  lastOfMonth.setFullYear(Number(rawYear), month, 0);
  return day <= lastOfMonth.getDate();
};

/** Every way a file can be refused, as i18n keys. */
export type CsvErrorKey =
  | 'datasource_import_error_not_csv'
  | 'datasource_import_error_too_large'
  | 'datasource_import_error_unreadable'
  | 'datasource_import_error_no_rows'
  | 'datasource_import_error_no_types_row'
  | 'datasource_import_error_no_data_rows'
  | 'datasource_import_error_no_columns'
  | 'datasource_import_error_blank_column'
  | 'datasource_import_error_ragged_rows'
  | 'datasource_import_error_unknown_type'
  | 'datasource_import_error_missing_column'
  | 'datasource_import_error_cells';

/** One cell that failed its column's type. Line and column are **1-based file
 *  coordinates** so they can be read straight off a spreadsheet: with the two
 *  header rows, the first record is line 3. */
export interface CsvCellError {
  line: number;
  column: number;
  type: string;
  value: string;
}

/** A refusal, ready to render: the key, whatever it needs interpolated, and —
 *  for cell failures — the full list for the console. */
export interface CsvFailure {
  errorKey: CsvErrorKey;
  params?: Record<string, string | number>;
  cellErrors?: CsvCellError[];
}

/** Note the STRING discriminant rather than a boolean `ok`. This project
 *  compiles with `strict: false` (tsconfig.json), and with strictNullChecks off
 *  TypeScript does not narrow a union on a boolean discriminant — `if (r.ok)`
 *  leaves `r` as the whole union and every field access fails. A string literal
 *  narrows either way, so don't "simplify" this back to a boolean. */
export type ParseResult =
  | { status: 'ok'; parsed: ParsedDataSourceCsv }
  | ({ status: 'error' } & CsvFailure);

const isCsv = (file: File) => file.name.toLowerCase().endsWith('.csv') || file.type === 'text/csv';

/** The dataTypes a column may declare — the picker's own set, so the importer
 *  and the Add-column dialog can never disagree about what exists. */
const KNOWN_TYPES: string[] = COLUMN_TYPES.map((type) => type.value);

/** Stop collecting past this many bad cells. The verdict was decided by the
 *  first one and the toast only summarises, so a 50k-row file must not run half
 *  a million regex tests to reach a conclusion it already had. */
const MAX_CELL_ERRORS = 100;

/**
 * Does one value satisfy a column's declared type? Empty is always allowed — a
 * blank is a missing value, not a malformed one, so whoever calls this owns the
 * question of whether blank is acceptable.
 *
 * Shared on purpose by the CSV importer AND the manual Add-row form
 * (DataSourceEntryModal). That is the point, not an implementation detail: a row
 * typed by hand must be exactly as valid as the same row imported from a file,
 * and a second copy of this switch is precisely how the two would drift apart.
 */
export const isCellValid = (type: string, value: string): boolean => {
  if (value === '') return true;
  switch (type) {
    case 'text':
      return true;
    case 'number':
      return !isNaN(Number(value));
    case 'geoCoordinates':
      return geoCoordinatesRegex.test(value);
    // Strict MM/DD/YYYY, as the legacy importer demanded. This was briefly
    // loosened to "anything Date can parse", which let "1200" through as the
    // year 1200 — see isValidMMDDYYYY for why that is the wrong trade here.
    case 'dateTime':
      return isValidMMDDYYYY(value);
    case 'phoneNumber':
      return phoneRegex.test(value);
    // Unreachable in practice — the type row is whitelisted before we get here
    // (see validateDataSourceCsv) — but kept as a backstop so an unchecked
    // caller can never have a column silently pass.
    default:
      return false;
  }
};

/**
 * Validate the whole file. Takes the RAW row matrix, headers included, because
 * the first checks are about the file's shape rather than its parsed summary —
 * and because the records must not outlive this call (see parseDataSourceCsv).
 * Pure and File-free, so it can be exercised without the DOM.
 *
 * The order is the legacy importer's (git 592b5417,
 * `EditDataSourceHeader.validateCSV`), minus its header-vs-schema check, which
 * has no meaning here: this import is what CREATES the schema.
 */
export const validateDataSourceCsv = (rows: string[][]): CsvFailure | null => {
  // 1. Header row, type row, and at least one record.
  if (rows.length === 0) return { errorKey: 'datasource_import_error_no_rows' };
  if (rows.length < 2) return { errorKey: 'datasource_import_error_no_types_row' };
  if (rows.length < 3) return { errorKey: 'datasource_import_error_no_data_rows' };

  const columnNames = rows[0].map((cell) => cell.trim());
  const dataTypes = rows[1].map((cell) => cell.trim());
  const width = columnNames.length;

  if (width === 0) return { errorKey: 'datasource_import_error_no_columns' };

  // 2. Every row the same width as the header — a short or long line means the
  //    values no longer line up with the columns they claim to fill.
  const raggedIndex = rows.findIndex((row) => row.length !== width);
  if (raggedIndex !== -1) {
    return {
      errorKey: 'datasource_import_error_ragged_rows',
      params: { line: raggedIndex + 1, expected: width, found: rows[raggedIndex].length },
    };
  }

  // 3. A blank name would become an unnamed column nothing can reference.
  if (columnNames.some((name) => !name))
    return { errorKey: 'datasource_import_error_blank_column' };

  // 4. Whitelist the type row BEFORE looking at cells. isCellValid rejects every
  //    non-empty cell of an unknown type, so skipping this would report one
  //    mistake once per record — 41 identical errors for a 41-row file.
  const unknownIndex = dataTypes.findIndex((type) => !KNOWN_TYPES.includes(type));
  if (unknownIndex !== -1) {
    return {
      errorKey: 'datasource_import_error_unknown_type',
      params: { type: dataTypes[unknownIndex] || '—', column: unknownIndex + 1 },
    };
  }

  // 5. Every cell against its column's type. Line numbers are file lines: the
  //    records start at line 3, hence the +3 on a 0-based record index.
  const cellErrors: CsvCellError[] = [];
  for (let record = 0; record < rows.length - 2 && cellErrors.length < MAX_CELL_ERRORS; record++) {
    const row = rows[record + 2];
    for (let column = 0; column < row.length && cellErrors.length < MAX_CELL_ERRORS; column++) {
      const type = dataTypes[column];
      const value = row[column];
      if (!isCellValid(type, value)) {
        cellErrors.push({ line: record + 3, column: column + 1, type, value });
      }
    }
  }
  if (cellErrors.length) {
    const [first] = cellErrors;
    return {
      errorKey: 'datasource_import_error_cells',
      params: {
        line: first.line,
        column: first.column,
        type: first.type,
        value: first.value,
        count: cellErrors.length,
      },
      cellErrors,
    };
  }

  return null;
};

/**
 * The extra rule the ENTRIES import needs, and the list-page import cannot have:
 * the file must carry the columns the data source already has.
 *
 * Set membership, not position — legacy tested `headerRow.includes(name)`, so a
 * reordered file is fine. Extra headers are fine too (the template adds `_id`);
 * only a MISSING one is an error, and the first is named so the message is
 * actionable.
 */
export const validateEntriesCsv = (
  rows: string[][],
  existingColumnNames: string[],
): CsvFailure | null => {
  const base = validateDataSourceCsv(rows);
  if (base) return base;

  const header = rows[0].map((cell) => cell.trim());
  const missing = existingColumnNames.find((name) => !header.includes(name));
  if (missing) {
    return { errorKey: 'datasource_import_error_missing_column', params: { name: missing } };
  }
  return null;
};

/** Read a picked file into `ParsedDataSourceCsv`, or the key of the reason not
 *  to. Never throws — every failure comes back as `{ status: 'error' }`.
 *
 *  `expectedColumns` switches on the entries-import rule above; omit it for the
 *  list-page import, which is what CREATES the columns. */
export const parseDataSourceCsv = (file: File, expectedColumns?: string[]): Promise<ParseResult> =>
  new Promise((resolve) => {
    if (!isCsv(file))
      return resolve({ status: 'error', errorKey: 'datasource_import_error_not_csv' });
    if (file.size > MAX_CSV_BYTES)
      return resolve({ status: 'error', errorKey: 'datasource_import_error_too_large' });

    Papa.parse<string[]>(file, {
      // No `header: true` — the file has TWO header rows and papaparse only
      // understands one, so the rows are taken raw and split here instead.
      //
      // 'greedy' also drops a record whose cells are ALL blank (`,,,,`), which
      // is what we want: that is not a record. A row with any real value
      // survives, and blanks inside it stay legal — isCellValid passes ''.
      skipEmptyLines: 'greedy',
      complete: ({ data }) => {
        const rows = data.filter((row) => row.length > 0);
        // Validate against the RAW rows, then throw them away: only the summary
        // travels on. The records would otherwise sit in React state for as long
        // as the modal is open, which for a 5 MB file is pure waste.
        const failure = expectedColumns
          ? validateEntriesCsv(rows, expectedColumns)
          : validateDataSourceCsv(rows);
        if (failure) return resolve({ status: 'error', ...failure });

        const columnNames = rows[0].map((cell) => cell.trim());
        resolve({
          status: 'ok',
          parsed: {
            columnNames,
            dataTypes: rows[1].map((cell) => cell.trim()),
            recordCount: rows.length - 2,
            columnCount: columnNames.length,
          },
        });
      },
      error: () => resolve({ status: 'error', errorKey: 'datasource_import_error_unreadable' }),
    });
  });

/**
 * The reserve endpoint (`media/upload-public-file`) hands back only a presigned
 * PUT url, but both import commits want the object's own address — and under the
 * usual S3 convention that address is everything before the signature query
 * string.
 *
 * ⚠️ This is the one part of the import flow INFERRED rather than specified, and
 * the legacy app disagrees: it PUT to `response.data.url` and sent that SAME url
 * unstripped. If a commit rejects `filePathUrl`, returning `uploadUrl` untouched
 * is the first thing to try. Shared by both import modals so the two can never
 * drift apart on it.
 */
export const publicUrlOf = (uploadUrl: string): string => uploadUrl.split('?')[0];

/** `data-source-template.csv` → `Data source template`. Seeds the name field so
 *  the common case is one keystroke-free field; the user can overwrite it. */
export const nameFromFile = (fileName: string): string => {
  const base = fileName
    .replace(/\.[^.]+$/, '')
    .replace(/[-_]+/g, ' ')
    .trim();
  return base ? base.charAt(0).toUpperCase() + base.slice(1) : '';
};
